import { logger } from './logger';

/**
 * Type guard for Cognito-style errors with code property
 */
interface CognitoStyleError {
  code?: string;
  name?: string;
  message?: string;
}

/**
 * Checks if an error is a transient Cognito error that should be retried.
 * Transient errors include:
 * - "invalid code or auth state" (session propagation delay)
 * - CodeMismatchException (timing issue)
 * - Network errors
 */
export const isTransientCognitoError = (error: unknown): boolean => {
  const err = error as CognitoStyleError;
  const message = err?.message?.toLowerCase() || '';
  const code = err?.code || err?.name || '';

  // Known transient error patterns
  const transientPatterns = [
    'invalid code or auth state',
    'codemismatchexception',
    'network',
    'devicenotfoundexception',
    'device_srp_auth',
  ];

  // Check if the error matches any transient pattern
  return transientPatterns.some(
    (pattern) =>
      message.includes(pattern) || code.toLowerCase().includes(pattern)
  );
};

/**
 * Checks if an error is specifically related to a stale or invalid Cognito device key.
 * When detected, the recovery action is to clear device keys from localStorage and retry.
 */
export const isDeviceKeyError = (error: unknown): boolean => {
  const err = error as CognitoStyleError;
  const message = err?.message?.toLowerCase() || '';
  const code = (err?.code || err?.name || '').toLowerCase();

  if (code === 'devicenotfoundexception') return true;
  if (message.includes('device_srp_auth')) return true;
  if (code === 'resourcenotfoundexception' && message.includes('device')) return true;

  return false;
};

/**
 * Retries an async function with exponential backoff for transient errors.
 * Only retries if the error is identified as transient.
 *
 * @param fn - The async function to retry
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param baseDelayMs - Base delay in milliseconds (default: 200)
 * @returns The result of the function
 * @throws The last error if all retries fail
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelayMs: number = 200
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry non-transient errors
      if (!isTransientCognitoError(error)) {
        throw error;
      }

      // Don't wait after the last attempt
      if (attempt < maxRetries) {
        const delay = baseDelayMs * Math.pow(2, attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};

/**
 * Helper function to handle NEW_PASSWORD_REQUIRED challenge
 * This is a more robust approach that handles the common "Cannot modify an already provided email" error
 */
export const completeNewPasswordChallenge = (
  cognitoUser: { completeNewPasswordChallenge: (password: string, attributes: Record<string, string>, callbacks: { onSuccess: (session: unknown) => void; onFailure: (error: Error) => void }) => void },
  newPassword: string,
  userAttributes: Record<string, string> = {},
  requiredAttributes: string[] = []
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    // Strategy 1: Try with empty attributes first (most common solution)
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session: unknown) => {
        resolve(session);
      },
      onFailure: () => {
        // Strategy 2: If empty attributes fail, try with filtered attributes
        const filteredAttributes: { [key: string]: string } = {};

        // Only include attributes that are explicitly required and not immutable
        const immutableAttributes = ['email', 'email_verified', 'phone_number_verified', 'sub'];

        requiredAttributes.forEach((attr: string) => {
          if (!immutableAttributes.includes(attr) && userAttributes[attr]) {
            filteredAttributes[attr] = userAttributes[attr];
          }
        });

        cognitoUser.completeNewPasswordChallenge(newPassword, filteredAttributes, {
          onSuccess: (session: unknown) => {
            resolve(session);
          },
          onFailure: (retryErr: Error) => {
            logger.error('Password change failed on retry', retryErr);
            reject(retryErr);
          },
        });
      },
    });
  });
};

/**
 * Common Cognito error messages and their user-friendly equivalents
 */
export const getCognitoErrorMessage = (error: { code?: string; name?: string; message?: string }): string => {
  const errorCode = error.code || error.name;
  const errorMessage = error.message || '';

  // Check for transient "invalid code or auth state" error first
  if (errorMessage.toLowerCase().includes('invalid code or auth state')) {
    return 'A temporary authentication issue occurred. Please try signing in again.';
  }

  // Check for device key errors
  if (errorMessage.toLowerCase().includes('device_srp_auth') || errorMessage.toLowerCase().includes('device')) {
    return 'Your device registration has expired. Please try signing in again.';
  }

  switch (errorCode) {
    case 'InvalidParameterException':
      if (errorMessage.includes('email')) {
        return 'Cannot modify the email address. Please contact support if you need to change your email.';
      }
      return 'Invalid parameters provided. Please check your input and try again.';

    case 'InvalidPasswordException':
      return 'Password does not meet requirements. Please use at least 8 characters.';

    case 'NotAuthorizedException':
      return 'Incorrect username or password. Please try again.';

    case 'UserNotConfirmedException':
      return 'Please verify your email address before signing in.';

    case 'UsernameExistsException':
      return 'An account with this email already exists. Please try signing in instead.';

    case 'LimitExceededException':
      return 'Too many attempts. Please wait a few minutes before trying again.';

    case 'CodeMismatchException':
      return 'A temporary authentication issue occurred. Please try again.';

    default:
      // Always return generic message to prevent leaking internal error details
      return 'An unexpected error occurred. Please try again.';
  }
};
